import styles from '@ui/components/ChoiceCard/ChoiceCard.module.scss';
import Image from '@ui/components/Image/Image';
import Card from '@ui/components/Card/Card';
import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon/FontAwesomeProIcon';
import Heading from '@ui/components/Heading/Heading';
import Counter from '@ui/components/Counter/Counter';
import Checkbox from '@ui/components/Checkbox/Checkbox';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getHeadingTag } from '../../utils';

const Label = ({ headingLevel = 4, title, subtitle }) => (
  <div className={styles.text}>
    <Heading
      as={getHeadingTag(headingLevel)}
      className={styles.title}
      noSpacing
      size="h6"
    >
      {title}
    </Heading>
    {!!subtitle && <div className={styles.subtitle}>{subtitle}</div>}
  </div>
);

Label.propTypes = {
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
};

const ChoiceCard = ({
  count,
  glyph = null,
  hasCheckbox = false,
  hasCounter = false,
  headingLevel = 4,
  image = null,
  max = 5,
  min = 0,
  onAction,
  onSelect,
  store,
  subtitle = null,
  title,
  ...props
}) => {
  let value;

  if (store) {
    value = store.useState('value');
  }

  /**
   * counter === 0 --> checked
   * counter > 0 --> unchecked
   */
  useEffect(() => {
    if (store) {
      store.setValue(count > 0);

      if (onSelect) {
        onSelect();
      }
    }
  }, [count]);

  /**
   * checked --> counter = 1
   * unchecked --> counter = 0
   */
  const onChange = e => {
    e.stopPropagation();

    if (onAction && !value && count === 0) {
      onAction('increment');
    }

    if (onAction && value && count > 0) {
      onAction('reset');
    }

    if (onSelect) {
      onSelect();
    }
  };

  return (
    <Card className={styles.container} {...props}>
      <div
        className={classNames(styles.content, {
          [styles.hasCheckbox]: hasCheckbox,
        })}
      >
        {image && (
          <Image
            alt={image.src?.title || image.alt}
            className={styles.image}
            role="presentation"
            src={image.src}
            srcSet={image.src?.srcSet || image.src}
          />
        )}
        {glyph && (
          <FontAwesomeProIcon
            className={styles.icon}
            fixedWidth
            icon={glyph}
            role="presentation"
          />
        )}
        {hasCheckbox ? (
          <Checkbox
            align="center"
            id={title}
            label={
              <Label
                headingLevel={headingLevel}
                subtitle={subtitle}
                title={title}
              />
            }
            name={title}
            onChange={onChange}
            store={store}
          />
        ) : (
          <Label
            headingLevel={headingLevel}
            subtitle={subtitle}
            title={title}
          />
        )}
        {hasCounter && (
          <Counter
            count={count}
            isNumberOnly
            max={max}
            min={min}
            noMargin
            onAction={onAction}
          >
            {count}
          </Counter>
        )}
      </div>
      {!hasCheckbox && (
        <FontAwesomeProIcon
          className={styles.chevron}
          fixedWidth
          icon="chevron-right"
          role="presentation"
        />
      )}
    </Card>
  );
};

ChoiceCard.propTypes = {
  glyph: PropTypes.string,
  hasCheckbox: PropTypes.bool,
  hasCounter: PropTypes.bool,
  icon: PropTypes.node,
  image: PropTypes.shape({
    src: PropTypes.string,
    srcSet: PropTypes.string,
    alt: PropTypes.string,
  }),
  max: PropTypes.number,
  min: PropTypes.number,
  onAction: PropTypes.func,
  onSelect: PropTypes.func,
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default ChoiceCard;
